/**
 * SPA Breadcrumb trail + BreadcrumbList JSON-LD injection.
 *
 * `items` — ordered list of `{ name, path? }`. The last item is always the
 * current page. Any item without a `path` renders as plain text.
 *
 * On mount, the component injects a matching `<script type="application/ld+json">`
 * BreadcrumbList into `<head>` so Googlebot picks it up even when the user
 * navigates via SPA. It's removed on unmount so we don't leak stale trails
 * between routes.
 */
import { useEffect } from "react";
import { Link } from "react-router-dom";

const SITE = "https://djspyofficial.com";
const SCRIPT_ID = "spa-breadcrumb-jsonld";

function injectJsonLd(items) {
  const elements = items.map((item, i) => {
    const el = {
      "@type": "ListItem",
      position: i + 1,
      name: item.name,
    };
    if (item.path) el.item = `${SITE}${item.path}`;
    return el;
  });
  const payload = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: elements,
  };
  let el = document.getElementById(SCRIPT_ID);
  if (!el) {
    el = document.createElement("script");
    el.type = "application/ld+json";
    el.id = SCRIPT_ID;
    document.head.appendChild(el);
  }
  el.textContent = JSON.stringify(payload);
}

/**
 * @param {{items: Array<{name:string, path?:string}>, testid?: string}} props
 */
export default function Breadcrumbs({ items, testid = "breadcrumbs" }) {
  useEffect(() => {
    injectJsonLd(items);
    return () => {
      const el = document.getElementById(SCRIPT_ID);
      if (el) el.remove();
    };
  }, [items]);

  return (
    <nav
      aria-label="Breadcrumb"
      data-testid={testid}
      className="mt-6 font-mono text-[10px] tracking-widest text-white/40"
    >
      {items.map((item, i) => {
        const isLast = i === items.length - 1;
        return (
          <span key={i}>
            {item.path && !isLast ? (
              <Link
                to={item.path}
                className="hover:text-white transition-colors"
                data-testid={`${testid}-item-${i}`}
              >
                {item.name.toUpperCase()}
              </Link>
            ) : (
              <span
                data-testid={`${testid}-item-${i}`}
                className={isLast ? "text-white/70" : ""}
                aria-current={isLast ? "page" : undefined}
              >
                {item.name.toUpperCase()}
              </span>
            )}
            {i < items.length - 1 && <span className="mx-2 text-white/25">/</span>}
          </span>
        );
      })}
    </nav>
  );
}
